import React, { useState } from 'react';
import { useDemo } from '../../state/DemoContext';
import { /* formatCurrency, */ formatDateTime } from '../../utils/helpers';
// import EncumbranceBadge from '../molecules/EncumbranceBadge'; // Unused import
import './aiagents.css';

const AIAgents = () => {
  const { state, actions } = useDemo();
  const [runningAgent, setRunningAgent] = useState(null);
  const [agentResults, setAgentResults] = useState({});

  const agents = [
    {
      id: 'encumbrance-detector',
      name: 'Encumbrance Detection Agent',
      description: 'Analyzes property encumbrances and lien positions to identify potential conflicts',
      icon: '🔍',
      status: 'active',
      lastRun: '2025-01-20T14:30:00Z',
      category: 'Risk Assessment'
    },
    {
      id: 'ltv-validator',
      name: 'LTV Validation Agent',
      description: 'Validates loan-to-value ratios against property valuations and market conditions',
      icon: '📊',
      status: 'active',
      lastRun: '2025-01-20T14:25:00Z',
      category: 'Risk Assessment'
    },
    {
      id: 'market-analyzer',
      name: 'Market Analysis Agent',
      description: 'Analyzes market conditions and property performance metrics',
      icon: '📈',
      status: 'active',
      lastRun: '2025-01-20T14:20:00Z',
      category: 'Market Intelligence'
    },
    {
      id: 'compliance-checker',
      name: 'Compliance Checker Agent',
      description: 'Ensures all transactions meet regulatory and compliance requirements',
      icon: '✅',
      status: 'active',
      lastRun: '2025-01-20T14:15:00Z',
      category: 'Compliance'
    }
  ];

  const handleRunAgent = async (agentId) => {
    setRunningAgent(agentId);
    
    // Simulate agent execution
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    let results = {};
    
    if (agentId === 'encumbrance-detector') {
      results = {
        conflictsFound: 2,
        propertiesAnalyzed: state.properties.length,
        listingsAnalyzed: state.listings.length,
        conflicts: [
          {
            id: 'conflict-1',
            propertyId: 'prop-1',
            listingId: 'listing-1',
            conflictType: 'Lien Position Mismatch',
            severity: 'high',
            description: 'Property has existing second lien but listing requests first lien position',
            recommendation: 'Adjust lien position or require lien subordination'
          },
          {
            id: 'conflict-2',
            propertyId: 'prop-2',
            listingId: 'listing-2',
            conflictType: 'Encumbrance Overlap',
            severity: 'medium',
            description: 'Multiple encumbrances detected that may affect priority',
            recommendation: 'Conduct title search and obtain lien priority confirmation'
          }
        ]
      };
      actions.runEncumbranceAgent();
    } else if (agentId === 'ltv-validator') {
      results = {
        validationsCompleted: state.listings.length,
        violationsFound: 1,
        violations: [
          {
            listingId: 'listing-3',
            currentLtv: 0.75,
            maxAllowedLtv: 0.65,
            severity: 'high',
            recommendation: 'Reduce loan amount or increase property valuation'
          }
        ]
      };
    } else if (agentId === 'market-analyzer') {
      results = {
        marketScore: 8.2,
        trend: 'positive',
        recommendations: [
          'Market conditions favor commercial real estate investments',
          'Cap rates are trending downward indicating strong demand',
          'Consider increasing exposure to industrial properties'
        ]
      };
    } else if (agentId === 'compliance-checker') {
      results = {
        checksCompleted: 15,
        violationsFound: 0,
        status: 'compliant',
        nextReviewDate: '2025-02-20T00:00:00Z'
      };
    }
    
    setAgentResults(prev => ({
      ...prev,
      [agentId]: {
        ...results,
        timestamp: new Date().toISOString()
      }
    }));
    
    setRunningAgent(null);
    actions.showToast(`${agents.find(a => a.id === agentId)?.name} completed successfully!`, 'success');
  };

  const getAgentStatusColor = (status) => {
    switch (status) {
      case 'active': return '#22C55E';
      case 'running': return '#F59E0B';
      case 'error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#22C55E';
      default: return '#6B7280';
    }
  };

  const renderAgentCard = (agent) => {
    const isRunning = runningAgent === agent.id;
    const results = agentResults[agent.id];
    
    return (
      <div key={agent.id} className="jo-xyz-demo-agent-card">
        <div className="jo-xyz-demo-agent-card__header">
          <div className="jo-xyz-demo-agent-card__info">
            <div className="jo-xyz-demo-agent-card__icon">{agent.icon}</div>
            <div className="jo-xyz-demo-agent-card__details">
              <h3 className="jo-xyz-demo-agent-card__name">{agent.name}</h3>
              <p className="jo-xyz-demo-agent-card__description">{agent.description}</p>
              <div className="jo-xyz-demo-agent-card__meta">
                <span className="jo-xyz-demo-agent-card__category">{agent.category}</span>
                <span
                  className="jo-xyz-demo-agent-card__status"
                  style={{ color: getAgentStatusColor(isRunning ? 'running' : agent.status) }}
                >
                  {isRunning ? 'Running...' : agent.status}
                </span>
              </div>
            </div>
          </div>
          <div className="jo-xyz-demo-agent-card__actions">
            <button 
              className="jo-xyz-demo-btn jo-xyz-demo-btn--primary jo-xyz-demo-btn--sm"
              onClick={() => handleRunAgent(agent.id)}
              disabled={isRunning}
            >
              {isRunning ? 'Running...' : 'Run Agent'}
            </button>
          </div>
        </div>
        
        {results && (
          <div className="jo-xyz-demo-agent-card__results">
            <div className="jo-xyz-demo-agent-results__header">
              <h4>Latest Results</h4>
              <span className="jo-xyz-demo-agent-results__timestamp">
                {formatDateTime(results.timestamp)}
              </span>
            </div>
            
            {agent.id === 'encumbrance-detector' && (
              <div className="jo-xyz-demo-encumbrance-results">
                <div className="jo-xyz-demo-results-summary">
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Properties Analyzed</span>
                    <span className="jo-xyz-demo-summary-value">{results.propertiesAnalyzed}</span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Listings Analyzed</span>
                    <span className="jo-xyz-demo-summary-value">{results.listingsAnalyzed}</span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Conflicts Found</span>
                    <span className="jo-xyz-demo-summary-value" style={{ color: results.conflictsFound > 0 ? '#EF4444' : '#22C55E' }}>
                      {results.conflictsFound}
                    </span>
                  </div>
                </div>

                {results.conflicts && results.conflicts.length > 0 && (
                  <div className="jo-xyz-demo-conflicts-list">
                    <h5>Detected Conflicts</h5>
                    {results.conflicts.map((conflict) => (
                      <div key={conflict.id} className="jo-xyz-demo-conflict-item">
                        <div className="jo-xyz-demo-conflict-item__header">
                          <span className="jo-xyz-demo-conflict-type">{conflict.conflictType}</span>
                          <span
                            className="jo-xyz-demo-conflict-severity"
                            style={{ color: getSeverityColor(conflict.severity) }}
                          >
                            {conflict.severity.toUpperCase()}
                          </span>
                        </div>
                        <p className="jo-xyz-demo-conflict-description">{conflict.description}</p>
                        <div className="jo-xyz-demo-conflict-recommendation">
                          <strong>Recommendation:</strong> {conflict.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {agent.id === 'ltv-validator' && (
              <div className="jo-xyz-demo-ltv-results">
                <div className="jo-xyz-demo-results-summary">
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Validations Completed</span>
                    <span className="jo-xyz-demo-summary-value">{results.validationsCompleted}</span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Violations Found</span>
                    <span className="jo-xyz-demo-summary-value" style={{ color: results.violationsFound > 0 ? '#EF4444' : '#22C55E' }}>
                      {results.violationsFound}
                    </span>
                  </div>
                </div>

                {results.violations && results.violations.length > 0 && (
                  <div className="jo-xyz-demo-violations-list">
                    <h5>LTV Violations</h5>
                    {results.violations.map((violation, index) => (
                      <div key={index} className="jo-xyz-demo-violation-item">
                        <div className="jo-xyz-demo-violation-item__header">
                          <span className="jo-xyz-demo-violation-listing">Listing {violation.listingId}</span>
                          <span
                            className="jo-xyz-demo-violation-severity"
                            style={{ color: getSeverityColor(violation.severity) }}
                          >
                            {violation.severity.toUpperCase()}
                          </span>
                        </div>
                        <div className="jo-xyz-demo-violation-details">
                          <span>Current LTV: {(violation.currentLtv * 100).toFixed(1)}%</span>
                          <span>Max Allowed: {(violation.maxAllowedLtv * 100).toFixed(1)}%</span>
                        </div>
                        <div className="jo-xyz-demo-violation-recommendation">
                          <strong>Recommendation:</strong> {violation.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {agent.id === 'market-analyzer' && (
              <div className="jo-xyz-demo-market-results">
                <div className="jo-xyz-demo-results-summary">
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Market Score</span>
                    <span className="jo-xyz-demo-summary-value">{results.marketScore}/10</span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Trend</span>
                    <span className="jo-xyz-demo-summary-value" style={{ color: results.trend === 'positive' ? '#22C55E' : '#EF4444' }}>
                      {results.trend.toUpperCase()}
                    </span>
                  </div>
                </div>

                {results.recommendations && (
                  <div className="jo-xyz-demo-recommendations-list">
                    <h5>Market Recommendations</h5>
                    <ul>
                      {results.recommendations.map((rec, index) => (
                        <li key={index}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {agent.id === 'compliance-checker' && (
              <div className="jo-xyz-demo-compliance-results">
                <div className="jo-xyz-demo-results-summary">
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Checks Completed</span>
                    <span className="jo-xyz-demo-summary-value">{results.checksCompleted}</span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Status</span>
                    <span className="jo-xyz-demo-summary-value" style={{ color: results.status === 'compliant' ? '#22C55E' : '#EF4444' }}>
                      {results.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span className="jo-xyz-demo-summary-label">Next Review</span>
                    <span className="jo-xyz-demo-summary-value">{formatDateTime(results.nextReviewDate)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="jo-xyz-demo-ai-agents-page">
      <div className="jo-xyz-demo-ai-agents-page__header">
        <h1>AI Agents</h1>
        <p className="jo-xyz-demo-ai-agents-page__subtitle">
          Automated intelligence agents that analyze risk, detect conflicts, and ensure compliance across your portfolio.
        </p>
      </div>

      <div className="jo-xyz-demo-ai-agents-page__content">
        <div className="jo-xyz-demo-agents-grid">
          {agents.map(renderAgentCard)}
        </div>

        <div className="jo-xyz-demo-ai-agents-page__info">
          <div className="jo-xyz-demo-card">
            <h3>How AI Agents Work</h3>
            <div className="jo-xyz-demo-info-content">
              <div className="jo-xyz-demo-info-item">
                <div className="jo-xyz-demo-info-icon">🔍</div>
                <div className="jo-xyz-demo-info-text">
                  <h4>Continuous Monitoring</h4>
                  <p>Agents continuously monitor your portfolio for changes, conflicts, and opportunities.</p>
                </div>
              </div>
              <div className="jo-xyz-demo-info-item">
                <div className="jo-xyz-demo-info-icon">⚡</div>
                <div className="jo-xyz-demo-info-text">
                  <h4>Real-Time Analysis</h4>
                  <p>Advanced algorithms analyze data in real-time to provide instant insights and alerts.</p>
                </div>
              </div>
              <div className="jo-xyz-demo-info-item">
                <div className="jo-xyz-demo-info-icon">🛡️</div>
                <div className="jo-xyz-demo-info-text">
                  <h4>Risk Mitigation</h4>
                  <p>Proactive identification of potential issues before they become problems.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAgents;
